// Phase 1C -- the Decision thread. One decision = one thread:
//   WHAT I FOUND -> WHY IT MATTERS -> WHERE THE OPPORTUNITY IS ->
//   WHAT I'D DO -> WHAT I PREPARED -> WHAT I NEED FROM YOU -> HOW I'LL MEASURE IT
// The first viewport answers "what happened / why / what Martian recommends /
// what I need to do". Evidence opens in place (no page hop); the conversation
// (Ask Martian) is scoped to this decision.
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { decisionsAPI } from '@/lib/api';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import { Chip, Disclosure, Eyebrow, Loading, PrimaryButton, SecondaryButton, TextButton, EmptyState, STATUS_COPY, formatDate } from '../ui';
import { IntelligenceChain, INTELLIGENCE_CHAIN_STAGES } from '@/brand';

const RESULT_COPY = {
  not_started: { label: 'Not started', tone: 'neutral', body: 'Measurement starts when you approve the work.' },
  too_early: { label: 'Too early', tone: 'warn', body: 'No check has run since you approved the work.' },
  improving: { label: 'Improving', tone: 'good', body: 'The latest check is better than the baseline.' },
  no_change: { label: 'No change', tone: 'neutral', body: 'The latest check matches the baseline.' },
  worse: { label: 'Worse', tone: 'bad', body: 'The latest check is below the baseline.' },
  // P0.2: the questions or search setup changed between the baseline and the
  // latest check, so the two numbers are not the same measurement.
  not_comparable: { label: 'Not comparable', tone: 'neutral', body: 'The way Martian measures this changed since the baseline, so the two checks cannot be compared.' },
};

function Step({ label, children, testId }) {
  return (
    <section className="grid md:grid-cols-[168px_1fr] gap-1 md:gap-6 py-6 border-t border-[var(--border)]" data-testid={testId}>
      <Eyebrow className="md:pt-1">{label}</Eyebrow>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

function Highlight({ text, names, brand }) {
  if (!text) return null;
  const terms = [...names, brand].filter(Boolean).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!terms.length) return <>{text}</>;
  const parts = text.split(new RegExp(`(${terms.join('|')})`, 'g'));
  return <>{parts.map((p, i) => (terms.some((t) => new RegExp(`^${t}$`).test(p)) ? <mark key={i} className="bg-[var(--accent-light)] text-[var(--text-primary)] rounded px-0.5">{p}</mark> : <span key={i}>{p}</span>))}</>;
}

function NeedsAction({ decision, onAsk, fp, navigate, compact }) {
  const needs = decision.needs_from_you || {};
  const work = decision.prepared_work;
  let primary = null;
  if (needs.action === 'review_work' || needs.action === 'retry_work') {
    primary = (
      <PrimaryButton onClick={() => navigate(fp(`/work?item=${encodeURIComponent(needs.work_id || work?.id)}`))} testId="decision-primary-action" size={compact ? 'md' : 'lg'}>
        Review prepared work <ArrowRight size={16} weight="bold" />
      </PrimaryButton>
    );
  } else if (needs.action === 'prepare_work') {
    primary = <PrimaryButton onClick={onAsk} testId="decision-primary-action">Ask Martian to prepare it</PrimaryButton>;
  }
  return (
    <div data-testid="decision-needs">
      <Eyebrow tone={primary ? 'accent' : 'muted'}>What I need from you</Eyebrow>
      <p className="mt-1.5 text-[15px] font-semibold leading-snug">{needs.label}</p>
      {primary && <div className="mt-4">{primary}</div>}
      {!compact && <div className="mt-3"><TextButton onClick={onAsk} testId="decision-ask">Ask Martian about this</TextButton></div>}
    </div>
  );
}

export default function DecisionPage() {
  const { decisionId } = useParams();
  const { workspaceId, displayName, openAsk } = useFounderWorkspace();
  const fp = useFounderPath();
  const navigate = useNavigate();
  const location = useLocation();
  const [decision, setDecision] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setDecision(null);
    setError(null);
    decisionsAPI.get(workspaceId, decisionId)
      .then((res) => { if (!cancelled) setDecision(res.data); })
      .catch((err) => { if (!cancelled) setError(err); });
    return () => { cancelled = true; };
  }, [workspaceId, decisionId]);

  useEffect(() => {
    if (decision && location.hash === '#evidence') {
      document.getElementById('evidence')?.scrollIntoView({ block: 'start' });
    }
  }, [decision, location.hash]);

  if (error) {
    return <EmptyState title="This decision isn't open any more" body="It may have been resolved, or the evidence behind it changed." action={<SecondaryButton to={fp('/decisions')}>All decisions</SecondaryButton>} />;
  }
  if (!decision) return <Loading label="Opening the decision…" />;

  const brand = displayName || 'your brand';
  const status = STATUS_COPY[decision.status] || STATUS_COPY.in_progress;
  const work = decision.prepared_work;
  const m = decision.measurement || {};
  const result = RESULT_COPY[m.result] || RESULT_COPY.not_started;
  const ev = decision.evidence || {};
  const ask = () => openAsk(decision.ask_context);
  const chainActiveKey = decision.status === 'needs_you' ? 'recommendation' : decision.status === 'measuring' ? 'measurement' : 'action';
  const chainOrder = INTELLIGENCE_CHAIN_STAGES.map((s) => s.key);
  const chainStates = Object.fromEntries(chainOrder.map((k, i) => [k, i < chainOrder.indexOf(chainActiveKey) ? 'complete' : i === chainOrder.indexOf(chainActiveKey) ? 'active' : 'pending']));

  return (
    <div data-testid="decision-page" className="pb-24 md:pb-0">
      <TextButton to={fp('/decisions')}><ArrowLeft size={14} /> Decisions</TextButton>

      <div className="mt-5 grid lg:grid-cols-[1fr_280px] gap-10">
        <article className="min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <Chip tone={status.tone} testId="decision-status">{status.label}</Chip>
            <span className="text-[12.5px] text-[var(--text-tertiary)]">{decision.channel}{decision.observed_at ? ` · observed ${formatDate(decision.observed_at)}` : ''}</span>
          </div>

          {/* ARC: where this decision sits in the Intelligence Chain, taken from
              its real status (needs you -> awaiting approval; in progress ->
              approved work under way; measuring -> being measured). */}
          <div className="mb-5 max-w-xl" data-testid="decision-chain">
            <IntelligenceChain compact activeKey={chainActiveKey} states={chainStates} label="Where this decision is in Martian's chain" />
          </div>

          {/* WHAT I FOUND */}
          <h1 className="font-display text-[26px] md:text-[32px] font-bold tracking-tight leading-[1.15] text-balance" data-testid="decision-found">{decision.found}</h1>

          {/* WHY IT MATTERS */}
          {decision.why && <p className="mt-4 text-[16px] text-[var(--text-secondary)] leading-relaxed max-w-[62ch]" data-testid="decision-why">{decision.why}</p>}

          {/* WHAT I'D DO -- visible in the first viewport */}
          {decision.recommendation && (
            <div className="mt-6 pl-4 border-l-2 border-[var(--accent)]" data-testid="decision-recommendation">
              <Eyebrow tone="accent">What I'd do</Eyebrow>
              <p className="mt-1 font-display text-[19px] font-semibold leading-snug max-w-[52ch] text-balance">{decision.recommendation}</p>
              {decision.recommendation_detail && <p className="mt-2 text-[14.5px] text-[var(--text-secondary)] max-w-[62ch] leading-relaxed">{decision.recommendation_detail}</p>}
            </div>
          )}

          <div className="mt-8">
            {/* WHERE THE OPPORTUNITY IS */}
            {decision.where && (
              <Step label="Where the opportunity is" testId="decision-where">
                <p className="text-[15px] leading-relaxed max-w-[62ch]">{decision.where}</p>
                {ev.sources?.examples?.length > 0 && (
                  <div className="mt-3">
                    <Disclosure label={`Pages AI relied on (${ev.sources.examples.length} examples)`} testId="decision-sources">
                      <ul className="space-y-2">
                        {ev.sources.examples.map((s) => (
                          <li key={s.url} className="text-[13.5px]">
                            <a href={s.url} target="_blank" rel="noreferrer noopener" className="text-[var(--text-primary)] hover:text-[var(--accent)] underline-offset-4 hover:underline">{s.title}</a>
                            <span className="block text-[12px] text-[var(--text-tertiary)]">{s.domain}</span>
                          </li>
                        ))}
                      </ul>
                    </Disclosure>
                  </div>
                )}
              </Step>
            )}

            {/* WHAT I PREPARED */}
            <Step label="What I prepared" testId="decision-prepared">
              {work ? (
                <div>
                  <p className="text-[12.5px] text-[var(--text-tertiary)]">{work.work_type === 'content_brief' ? 'Content brief' : 'Prepared work'} · {formatDate(work.created_at)}</p>
                  <p className="mt-1 text-[16px] font-semibold leading-snug max-w-[60ch]">{work.headline || work.title}</p>
                  {work.what_to_create && <p className="mt-2 text-[14px] text-[var(--text-secondary)] max-w-[62ch] leading-relaxed line-clamp-3">{work.what_to_create}</p>}
                  {work.grounded && (
                    <p className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] text-[var(--martian-mint-ink,#3F7A64)]" data-testid="decision-grounded">
                      <CheckCircle size={14} weight="fill" /> Checked against your business: {work.grounded_in.map((f) => f.replace(/_/g, ' ')).join(', ')}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-[14.5px] text-[var(--text-secondary)]">Nothing yet. Ask Martian to prepare the work when you're ready.</p>
              )}
            </Step>

            {/* HOW I'LL MEASURE IT */}
            <Step label="How I'll measure it" testId="decision-measure">
              {m.supported ? (
                <div>
                  <p className="text-[15px]">{m.metric}</p>
                  <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-[13px]">
                    <div><dt className="text-[var(--text-tertiary)]">Baseline</dt><dd className="mt-1 font-semibold tabular-nums">{m.baseline ? `${m.baseline.value} / ${m.baseline.of}` : '—'}</dd><dd className="text-[12px] text-[var(--text-tertiary)]">{m.baseline ? formatDate(m.baseline.observed_at) : ''}</dd></div>
                    <div><dt className="text-[var(--text-tertiary)]">Work approved</dt><dd className="mt-1 font-semibold">{m.intervention ? formatDate(m.intervention.at) : 'Not yet'}</dd></div>
                    <div><dt className="text-[var(--text-tertiary)]">Next check</dt><dd className="mt-1 font-semibold">{m.next_check ? formatDate(m.next_check) : 'Not scheduled'}</dd></div>
                    <div><dt className="text-[var(--text-tertiary)]">Result</dt><dd className="mt-1"><Chip tone={result.tone} testId="decision-result">{result.label}</Chip></dd></div>
                  </dl>
                  {m.next_check_blocked_by === 'watchlist_not_approved' && (
                    <p className="mt-4 text-[13.5px] text-[var(--text-secondary)]">
                      Weekly checks start when you approve the questions Martian watches. <TextButton to={fp('/business/watchlist')} tone="accent">Review questions</TextButton>
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-[14.5px] text-[var(--text-secondary)]">{m.note}</p>
              )}
            </Step>

            {/* EVIDENCE -- in place, not another page */}
            <section id="evidence" className="py-6 border-t border-[var(--border)] scroll-mt-6" data-testid="decision-evidence">
              <Disclosure label="Evidence" defaultOpen={location.hash === '#evidence'}>
                {ev.answers?.length > 0 && (
                  <div>
                    <p className="text-[13px] text-[var(--text-secondary)] mb-3">AI answers from the latest check ({formatDate(ev.observed_at)}). Names Martian recognised are highlighted.</p>
                    <ol className="space-y-5">
                      {ev.answers.map((a, i) => (
                        <li key={a.id || i} className="text-[14px]" data-testid="decision-answer">
                          <p className="text-[12.5px] text-[var(--text-tertiary)]">
                            Asked: “{a.asked}”
                            {a.asked_with_old_wording && <span className="ml-1">(before Martian rewrote your questions)</span>}
                          </p>
                          <p className="mt-1 leading-relaxed max-w-[68ch]"><Highlight text={a.answer} names={a.names} brand={brand} /></p>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {/* A prompt that could not be checked is unknown, never "doesn't mention". */}
                            {a.state === 'UNKNOWN' || a.mentions_brand == null
                              ? <Chip tone="neutral" testId="decision-answer-unchecked">This question couldn't be checked</Chip>
                              : <Chip tone={a.mentions_brand ? 'good' : 'bad'}>{a.mentions_brand ? `Mentions ${brand}` : `Doesn't mention ${brand}`}</Chip>}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {ev.facts?.length > 0 && (
                  <dl className="grid grid-cols-2 gap-3 text-[13.5px]">
                    {ev.facts.map((f) => <div key={f.label}><dt className="text-[var(--text-tertiary)] capitalize">{f.label}</dt><dd className="font-semibold">{String(f.value)}</dd></div>)}
                  </dl>
                )}
                {decision.facets?.length > 0 && (
                  <div className="mt-6">
                    <Eyebrow>Also behind this decision</Eyebrow>
                    <ul className="mt-2 space-y-2">
                      {decision.facets.map((f) => <li key={f.type} className="text-[13.5px]"><span className="font-medium">{f.title}.</span> <span className="text-[var(--text-secondary)]">{f.detail}</span></li>)}
                    </ul>
                  </div>
                )}
                <div className="mt-5"><TextButton to={fp('/evidence')}>Everything Martian knows</TextButton></div>
              </Disclosure>
            </section>
          </div>
        </article>

        {/* WHAT I NEED FROM YOU -- always visible */}
        <aside className="hidden lg:block">
          <div className="sticky top-8 rounded-[var(--radius-card-soft)] border border-[var(--border)] bg-[var(--surface)] p-5 shadow-[var(--shadow-card)]">
            <NeedsAction decision={decision} onAsk={ask} fp={fp} navigate={navigate} />
          </div>
        </aside>
      </div>

      {/* Mobile: the one action stays reachable above the tab bar */}
      <div className="lg:hidden fixed inset-x-0 bottom-14 md:bottom-0 z-30 border-t border-[var(--border)] bg-[var(--surface)] px-5 py-3" data-testid="decision-mobile-action">
        <NeedsAction decision={decision} onAsk={ask} fp={fp} navigate={navigate} compact />
      </div>
    </div>
  );
}
